import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getListSecretsQueryKey,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import { unmarkManagedOAuthReturn } from '../oauth-resume';

/**
 * Undoes a new OAuth credential's sign-in that can't leave the page (e.g. the
 * provider URL failed validation): drops the return marker and deletes the
 * credential, which would otherwise stay pending.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `abandonSignIn(credentialId, reason)`.
 */
export function useAbandonOAuthSignIn(
  organizationId: string,
  workspaceId: string,
) {
  const queryClient = useQueryClient();
  // New OAuth credentials are always created in the workspace.
  const { mutateAsync: deleteSecret } = useDeleteSecret2();

  return useCallback(
    (credentialId: string, reason: unknown) => {
      unmarkManagedOAuthReturn();
      captureException(reason as Error, {
        tags: { module: 'credentials', action: 'abandonOAuthSignIn' },
      });
      void deleteSecret({ organizationId, workspaceId, id: credentialId })
        .catch(deleteError =>
          captureException(deleteError, {
            tags: {
              module: 'credentials',
              action: 'deleteAbandonedCredential',
            },
          }),
        )
        .finally(() =>
          queryClient.invalidateQueries({
            queryKey: getListSecretsQueryKey(organizationId),
          }),
        );
    },
    [organizationId, workspaceId, deleteSecret, queryClient],
  );
}
